/**
 * Personalization Preview Carousel Widget
 * Showcases personalization options in an auto-rotating carousel
 * Supports multiple themes: 'generic' (default), 'christmas'
 *
 * Features:
 * - 2-3 personalisation cards (number plate, letter, video - video only in christmas mode)
 * - Auto-rotates every 6 seconds
 * - Scroll snap for smooth canvas-like scrolling
 * - Native swipe gestures on mobile (via scroll snap)
 * - Navigation arrows visible on desktop only
 * - Animated dots with morphing effect
 * - Clean black & white design with professional SVG icons
 * - "Learn More" modal with comprehensive information
 * - Pause on hover
 * - Keyboard navigation support
 * - ARIA labels for accessibility
 * - Theme-aware content (generic vs christmas)
 *
 * @version 2.0.0
 */

(function(window) {
  'use strict';

  // Theme configurations for the letter slide
  const LETTER_THEMES = {
    generic: {
      title: 'FREE Personalised Letter',
      description: 'FREE with every ride-on - beautiful templates',
      modalTitle: 'Personalised Letter',
      modalDescription: 'Every ride-on includes a FREE personalised letter - the perfect finishing touch',
      showFestiveOverlay: false,
      showSnowAnimation: false
    },
    christmas: {
      title: 'Magical Santa Letter',
      description: 'FREE with every ride-on - beautiful templates',
      modalTitle: 'Magical Santa Letter',
      modalDescription: 'Every ride-on includes a FREE personalised letter from Santa - the perfect finishing touch',
      showFestiveOverlay: true,
      showSnowAnimation: true
    }
  };

  class PersonalizationCarouselWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      this.currentSlide = 0;
      this.slides = [];
      this.isAutoPlaying = true;
      this.isManuallyPaused = false; // Track if user manually paused
      this.autoPlayInterval = null;
      this.rotationDelay = config.rotationDelay || 6000; // 6 seconds
      this.lazyLoad = config.lazyLoad !== false;
      this.modalId = `personalization-modal-${config.id}`;
      // Santa video URL - use provided URL or default to Shopify CDN video
      this.santaVideoUrl = config.santaVideoUrl || 'https://cdn.shopify.com/videos/c/o/v/d150759c29aa4248aa51c7c65329ad82.mp4';

      // Theme will be set during init
      this.letterPromoTheme = 'generic';

      // ========================================
      // SNOW ANIMATION TOGGLE - Now controlled by theme
      // ========================================
      this.enableSnowAnimation = config.enableSnowAnimation !== false; // Default: true, but theme can override
    }

    /**
     * Get current letter theme configuration
     */
    getLetterThemeConfig() {
      return LETTER_THEMES[this.letterPromoTheme] || LETTER_THEMES.generic;
    }

    /**
     * Initialise widget - setup slide data with real product pricing
     */
    async init() {
      // Get theme from bundle config
      this.letterPromoTheme = this.bundleConfig?.settings?.letterPromoTheme || 'generic';
      const letterTheme = this.getLetterThemeConfig();

      // Get personalisation addons from bundle config
      const personalizationAddons = this.bundleConfig?.addons?.personalization || [];

      // Find the specific products
      const numberPlateAddon = personalizationAddons.find(a => a.productHandle === 'number-plate-addon');
      const letterAddon = personalizationAddons.find(a => a.productHandle === 'personalised-letter');
      const videoAddon = personalizationAddons.find(a => a.productHandle === 'personalised-animated-video');

      // Fetch product data for each (including Letter for image)
      const numberPlateProduct = numberPlateAddon ? await this.fetchProductByHandle(numberPlateAddon.productHandle) : null;
      const letterProduct = letterAddon ? await this.fetchProductByHandle(letterAddon.productHandle) : null;
      const videoProduct = videoAddon ? await this.fetchProductByHandle(videoAddon.productHandle) : null;

      // Store Letter product for image updates
      this.santaLetterProduct = letterProduct;

      // Define personalisation cards - video only shown in christmas mode
      this.slides = [
        {
          id: 'number-plate',
          title: numberPlateAddon?.name || 'Personalised Number Plate',
          description: '45 country flags - their name on their car',
          icon: 'plate',
          product: numberPlateProduct,
          addon: numberPlateAddon,
          badge: null,
          videoBackground: null,
          productImage: numberPlateProduct?.featured_image || numberPlateProduct?.images?.[0] || null,
          showProductImageBg: true // Show product image as background
        },
        {
          id: 'letter',
          title: letterTheme.title,
          description: letterTheme.description,
          icon: 'letter',
          price: 'FREE',
          addon: letterAddon,
          badge: null,
          videoBackground: null,
          // Product image for background display
          productImage: letterProduct?.featured_image || letterProduct?.images?.[0] || null,
          showFestiveOverlay: letterTheme.showFestiveOverlay, // Only in christmas mode
          showProductImageBg: !letterTheme.showFestiveOverlay // Use product image bg in generic mode
        }
      ];

      // Only add Santa Video slide in christmas mode
      if (this.letterPromoTheme === 'christmas' && videoAddon) {
        this.slides.push({
          id: 'santa-video',
          title: videoAddon?.name || 'Personalised Santa Video',
          description: 'Animated video of Santa in YOUR living room',
          icon: 'video',
          product: videoProduct,
          addon: videoAddon,
          badge: 'Premium',
          videoBackground: this.santaVideoUrl // Santa video background
        });
      }

      // Override snow animation based on theme
      if (!letterTheme.showSnowAnimation) {
        this.enableSnowAnimation = false;
      }
    }

    /**
     * Fetch product data by handle
     */
    async fetchProductByHandle(handle) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.error('[PersonalizationCarousel] Error fetching product:', error);
        return null;
      }
    }

    /**
     * Get pricing display for a slide
     */
    getPricingDisplay(slide, hasVideo = false) {
      if (slide.price === 'FREE') {
        return `
          <div style="display: inline-flex; align-items: center; justify-content: center; padding: var(--sp-2) var(--sp-4); background: ${hasVideo ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; color: ${hasVideo ? 'rgb(var(--color-drawer-text))' : 'rgb(var(--color-drawer-background))'}; font-weight: 700; font-size: var(--text-base); border-radius: var(--rounded-button); border: 1px solid ${hasVideo ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; ${hasVideo ? 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);' : ''}">
            FREE
          </div>
        `;
      }

      if (!slide.product) return '';

      const price = slide.product.price / 100;
      const compareAtPrice = slide.product.compare_at_price ? slide.product.compare_at_price / 100 : null;

      // Check if there's a discount from bundle config
      const currentTier = this.getCurrentTier();
      const discount = currentTier && currentTier.discountPercent ? currentTier.discountPercent : 0;

      if (compareAtPrice && compareAtPrice > price) {
        // Product has a compare-at price (already on sale)
        return `
          <div style="display: flex; align-items: center; gap: var(--sp-2); justify-content: center;">
            <span style="text-decoration: line-through; color: ${hasVideo ? 'rgba(255, 255, 255, 0.7)' : 'rgb(var(--color-drawer-text) / 0.5)'}; font-size: var(--text-sm); ${hasVideo ? 'text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);' : ''}">
              £${compareAtPrice.toFixed(2)}
            </span>
            <span style="font-weight: 700; font-size: var(--text-lg); color: ${hasVideo ? 'rgb(255, 255, 255)' : 'rgb(var(--color-drawer-text))'}; ${hasVideo ? 'text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);' : ''}">
              £${price.toFixed(2)}
            </span>
          </div>
        `;
      } else if (discount > 0) {
        // Bundle discount applies
        const discountedPrice = price * (1 - discount / 100);
        return `
          <div style="display: flex; align-items: center; gap: var(--sp-2); justify-content: center;">
            <span style="text-decoration: line-through; color: ${hasVideo ? 'rgba(255, 255, 255, 0.7)' : 'rgb(var(--color-drawer-text) / 0.5)'}; font-size: var(--text-sm); ${hasVideo ? 'text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);' : ''}">
              £${price.toFixed(2)}
            </span>
            <span style="font-weight: 700; font-size: var(--text-lg); color: ${hasVideo ? 'rgb(255, 255, 255)' : 'rgb(var(--color-drawer-text))'}; ${hasVideo ? 'text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);' : ''}">
              £${discountedPrice.toFixed(2)}
            </span>
          </div>
        `;
      } else {
        // Regular price
        return `
          <div style="font-weight: 700; font-size: var(--text-lg); color: ${hasVideo ? 'rgb(255, 255, 255)' : 'rgb(var(--color-drawer-text))'}; ${hasVideo ? 'text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);' : ''}">
            £${price.toFixed(2)}
          </div>
        `;
      }
    }

    /**
     * Get current bundle tier (if any)
     */
    getCurrentTier() {
      // This would need to be integrated with cart state
      // For now, return null (no active tier)
      return null;
    }

    /**
     * Render the carousel
     */
    render() {
      this.setContent(this.buildCarouselHTML() + this.buildModalHTML());
      this.attachEventListeners();
      this.startAutoPlay();
      this.emit('personalization-carousel:rendered');
    }

    /**
     * Build carousel HTML
     */
    buildCarouselHTML() {
      // Dark theme colors
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';
      const borderColor = 'rgb(var(--color-drawer-background) / 0.2)';

      return `
        <div style="background: ${bgColor}; border-radius: var(--rounded-card); padding: var(--sp-5); position: relative; overflow: hidden;">

          <!-- Play/Pause Button (Bottom Left) -->
          <button
            class="pw-carousel-play-pause"
            aria-label="Pause auto-rotation"
            style="position: absolute; bottom: var(--sp-4); left: var(--sp-4); width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; background: transparent; border: 1px solid ${borderColor}; border-radius: 50%; cursor: pointer; transition: all 0.2s ease; z-index: 10;">
            <svg class="pw-carousel-pause-icon" style="width: 14px; height: 14px; color: ${textColor};" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"></path>
            </svg>
            <svg class="pw-carousel-play-icon" style="width: 14px; height: 14px; color: ${textColor}; display: none;" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"></path>
            </svg>
          </button>

          <!-- Header -->
          <div style="display: flex; align-items: flex-start; gap: var(--sp-3); margin-bottom: var(--sp-5);">
            <!-- Icon with gradient -->
            <div style="display: flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: rgb(var(--color-drawer-background) / 0.15); border-radius: var(--rounded-card); flex-shrink: 0;">
              <svg style="width: 1.25rem; height: 1.25rem;" viewBox="0 0 24 24" fill="none" stroke="url(#iconGradient)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <defs>
                  <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="5%" stop-color="rgba(249, 119, 255, 1)" />
                    <stop offset="50%" stop-color="rgba(54, 212, 255, 1)" />
                    <stop offset="100%" stop-color="rgba(43, 252, 135, 1)" />
                  </linearGradient>
                </defs>
                <path d="M12 3l1.5 3.5L17 8l-3.5 1.5L12 13l-1.5-3.5L7 8l3.5-1.5L12 3z"/>
                <path d="M5 16l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/>
                <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/>
              </svg>
            </div>
            <!-- Text -->
            <div>
              <h3 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-lg); line-height: 1.2; margin: 0 0 var(--sp-1) 0; background: linear-gradient(57deg, rgba(249, 119, 255, 1) 5%, rgba(54, 212, 255, 1) 20%, rgba(43, 252, 135, 1) 50%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
                Make It Personal
              </h3>
              <p style="font-size: var(--text-sm); line-height: 1.5; color: ${mutedColor}; margin: 0;">
                Add that special touch to your ride-on
              </p>
            </div>
          </div>

          <!-- Carousel Track (scroll snap) -->
          <div class="pw-carousel-track" role="region" aria-label="Personalisation options carousel" style="display: flex; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; scroll-behavior: smooth; gap: var(--sp-4); border-radius: var(--rounded-card); margin-bottom: var(--sp-4); -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none;">
            ${this.slides.map((slide, index) => this.renderSlide(slide, index)).join('')}
          </div>

          <!-- Controls Row (arrows + dots) -->
          <div style="display: flex; align-items: center; justify-content: center; gap: var(--sp-4); margin-bottom: var(--sp-4);">

            <!-- Previous Arrow (hidden on mobile) -->
            <button
              class="pw-carousel-arrow pw-carousel-arrow--prev"
              aria-label="Previous slide"
              style="width: var(--sp-10); height: var(--sp-10); align-items: center; justify-content: center; background: ${bgColor}; border: 2px solid ${borderColor}; border-radius: 50%; cursor: pointer; transition: all 0.2s ease; display: none;">
              <svg style="width: var(--sp-5); height: var(--sp-5); color: ${textColor};" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>

            <!-- Dots Indicator with animated active state -->
            <div class="pw-carousel-dots" role="tablist" aria-label="Slide navigation" style="display: flex; gap: var(--sp-2); align-items: center;">
              ${this.slides.map((_, index) => `
                <button
                  class="pw-carousel-dot ${index === 0 ? 'pw-carousel-dot--active' : ''}"
                  role="tab"
                  aria-label="Go to slide ${index + 1}"
                  aria-selected="${index === 0}"
                  data-slide="${index}"
                  style="height: 10px; border-radius: 5px; border: none; background: ${index === 0 ? textColor : mutedColor}; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); width: ${index === 0 ? '24px' : '10px'};">
                </button>
              `).join('')}
            </div>

            <!-- Next Arrow (hidden on mobile) -->
            <button
              class="pw-carousel-arrow pw-carousel-arrow--next"
              aria-label="Next slide"
              style="width: var(--sp-10); height: var(--sp-10); align-items: center; justify-content: center; background: ${bgColor}; border: 2px solid ${borderColor}; border-radius: 50%; cursor: pointer; transition: all 0.2s ease; display: none;">
              <svg style="width: var(--sp-5); height: var(--sp-5); color: ${textColor};" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>

          </div>

          <!-- Learn More Button -->
          <div style="display: flex; justify-content: flex-end;">
            <button
              type="button"
              class="pw-learn-more pw-carousel-learn-more"
              aria-controls="${this.modalId}"
              aria-expanded="false"
              aria-label="Learn more about personalisation options">
              <span>Learn more</span>
              <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>

        </div>
      `;
    }

    /**
     * Render individual slide
     */
    renderSlide(slide, index) {
      const isActive = index === this.currentSlide;
      const hasVideo = slide.videoBackground;
      const hasFestiveOverlay = slide.showFestiveOverlay && slide.productImage;
      const hasProductImageBg = slide.showProductImageBg && slide.productImage && !hasFestiveOverlay;
      const showSnow = this.enableSnowAnimation && hasFestiveOverlay;
      const needsWhiteText = hasVideo || hasFestiveOverlay || hasProductImageBg; // Use white text for dark backgrounds

      return `
        <div
          class="pw-carousel-slide ${isActive ? 'pw-carousel-slide--active' : ''}"
          data-slide-index="${index}"
          role="tabpanel"
          aria-hidden="${!isActive}"
          style="flex: 0 0 100%; scroll-snap-align: start; scroll-snap-stop: always;">

          <div
            class="pw-carousel-card"
            role="button"
            tabindex="0"
            aria-controls="${this.modalId}"
            aria-expanded="false"
            aria-label="Click to learn more about ${slide.title}"
            style="background: ${hasFestiveOverlay ? 'linear-gradient(135deg, #C41E3A 0%, #165B33 100%)' : 'rgb(var(--color-drawer-text) / 0.02)'}; border: 1px solid rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card); padding: var(--sp-6); text-align: center; min-height: 280px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; position: relative; overflow: hidden; cursor: pointer; transition: all 0.2s ease;">

            ${hasProductImageBg ? `
              <!-- Product Image Background with darkening filter -->
              <img src="${slide.productImage}" alt="${slide.title}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; border-radius: var(--rounded-card); filter: brightness(0.5);" loading="lazy">
              <!-- Additional dark overlay for better text contrast -->
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.3); z-index: 0; border-radius: var(--rounded-card);"></div>
            ` : ''}

            ${hasFestiveOverlay ? `
              <!-- Product Image Background with darkening filter (same as number plate) -->
              <img src="${slide.productImage}" alt="Santa Letter" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; border-radius: var(--rounded-card); filter: brightness(0.5);" loading="lazy">
              <!-- Additional dark overlay for better text contrast -->
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.3); z-index: 1; border-radius: var(--rounded-card);"></div>

              ${showSnow ? `
              <!-- Snow Animation -->
              <div class="pw-slp-snowflakes" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; overflow: hidden; z-index: 2;">
                <div class="pw-slp-snowflake" style="left: 15%; animation-delay: 0s;">❄</div>
                <div class="pw-slp-snowflake" style="left: 45%; animation-delay: 1.2s;">❄</div>
                <div class="pw-slp-snowflake" style="left: 75%; animation-delay: 2.5s;">❄</div>
              </div>
              ` : ''}
            ` : ''}

            ${hasVideo ? `
              <!-- Video Background -->
              <video
                class="pw-carousel-video-bg"
                autoplay
                loop
                muted
                playsinline
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0; border-radius: var(--rounded-card);">
                <source src="${slide.videoBackground}" type="video/mp4">
              </video>
              <!-- Dark overlay for video (same approach as images) -->
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.5); z-index: 1; border-radius: var(--rounded-card);"></div>
            ` : ''}

            <!-- Bottom Gradient Overlay for Text Contrast - Applied to ALL cards -->
            <div style="position: absolute; bottom: 0; left: 0; width: 100%; height: 60%; background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.95) 100%); z-index: 10; border-radius: var(--rounded-card); pointer-events: none;"></div>

            <!-- Content Wrapper (above gradient) -->
            <div style="position: relative; z-index: 20; display: flex; flex-direction: column; align-items: center; width: 100%;">

              ${slide.badge ? `
                <div style="position: absolute; top: calc(var(--sp-3) * -1); right: calc(var(--sp-3) * -1); padding: var(--sp-1) var(--sp-2d5); background: ${needsWhiteText ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; color: ${needsWhiteText ? '#333' : 'rgb(var(--color-drawer-background))'}; font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; border-radius: var(--rounded-button); box-shadow: ${needsWhiteText ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none'};">
                  ${slide.badge}
                </div>
              ` : ''}

              <!-- Title -->
              <h4 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: clamp(1rem, 4vw, var(--text-lg)); margin: 0 0 var(--sp-2) 0; background: linear-gradient(57deg, rgba(249, 119, 255, 1) 5%, rgba(54, 212, 255, 1) 20%, rgba(43, 252, 135, 1) 50%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
                ${slide.title}
              </h4>

              <!-- Description -->
              <p style="font-size: var(--text-sm); color: ${needsWhiteText ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text) / 0.7)'}; margin: 0 0 var(--sp-3) 0; max-width: 320px; ${needsWhiteText ? 'text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);' : ''}">
                ${slide.description}
              </p>

              <!-- Dynamic Pricing or FREE badge for Letter -->
              ${slide.id === 'letter' ? this.getFreeBadge(needsWhiteText) : this.getPricingDisplay(slide, needsWhiteText)}

              <!-- Limited Time Offer badge for Letter (inline below FREE) -->
              ${slide.id === 'letter' ? this.getShippingBadgeInline(needsWhiteText) : ''}

            </div>

          </div>
        </div>
      `;
    }

    /**
     * Get shipping message if configured (OLD METHOD - KEPT FOR COMPATIBILITY)
     */
    getShippingMessage(needsWhiteText = false) {
      const shippingMessage = this.bundleConfig?.settings?.santaLetterShippingMessage;
      if (!shippingMessage) return '';

      const textColor = needsWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'rgb(var(--color-drawer-text) / 0.8)';
      const bgColor = needsWhiteText ? 'rgba(255, 255, 255, 0.15)' : 'rgb(var(--color-drawer-text) / 0.05)';
      const borderColor = needsWhiteText ? 'rgba(255, 255, 255, 0.3)' : 'rgb(var(--color-base-button))';
      const iconColor = needsWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'rgb(var(--color-base-button))';

      return `
        <div style="margin-top: var(--sp-3); padding: var(--sp-2) var(--sp-3); background: ${bgColor}; border-left: 3px solid ${borderColor}; border-radius: 4px; display: flex; align-items: center; gap: var(--sp-2);">
          <svg style="width: 14px; height: 14px; flex-shrink: 0; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p style="margin: 0; font-size: 0.75rem; font-weight: 600; color: ${textColor};">
            ${shippingMessage}
          </p>
        </div>
      `;
    }

    /**
     * Get shipping badge for top of Santa Letter card (OLD - REPLACED WITH INLINE VERSION)
     */
    getShippingBadge(needsWhiteText = false) {
      const shippingMessage = this.bundleConfig?.settings?.santaLetterShippingMessage;
      if (!shippingMessage) return '';

      return `
        <div style="position: absolute; top: calc(var(--sp-3) * -1); left: calc(var(--sp-3) * -1); padding: var(--sp-1d5) var(--sp-3); background: ${needsWhiteText ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; color: ${needsWhiteText ? '#333' : 'rgb(var(--color-drawer-background))'}; font-size: var(--text-2xs); font-weight: 600; border-radius: var(--rounded-button); box-shadow: ${needsWhiteText ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none'}; display: flex; align-items: center; gap: 6px;">
          <svg style="width: 12px; height: 12px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>${shippingMessage}</span>
        </div>
      `;
    }

    /**
     * Get shipping badge inline (below FREE button) for Santa Letter card
     */
    getShippingBadgeInline(needsWhiteText = false) {
      const shippingMessage = this.bundleConfig?.settings?.santaLetterShippingMessage;
      if (!shippingMessage) return '';

      const textColor = needsWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'rgb(var(--color-drawer-text) / 0.8)';
      const bgColor = needsWhiteText ? 'rgba(255, 255, 255, 0.15)' : 'rgb(var(--color-drawer-text) / 0.05)';
      const borderColor = needsWhiteText ? 'rgba(255, 255, 255, 0.3)' : 'rgb(var(--color-base-button))';
      const iconColor = needsWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'rgb(var(--color-base-button))';

      return `
        <div style="margin-top: var(--sp-3); padding: var(--sp-2) var(--sp-3); background: ${bgColor}; border-left: 3px solid ${borderColor}; border-radius: 4px; display: flex; align-items: center; gap: var(--sp-2);">
          <svg style="width: 14px; height: 14px; flex-shrink: 0; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p style="margin: 0; font-size: 0.75rem; font-weight: 600; color: ${textColor};">
            ${shippingMessage}
          </p>
        </div>
      `;
    }

    /**
     * Get FREE badge for Santa Letter (smaller, inline style)
     */
    getFreeBadge(needsWhiteText = false) {
      return `
        <div style="display: inline-flex; align-items: center; justify-content: center; padding: var(--sp-1d5) var(--sp-4); background: ${needsWhiteText ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; color: ${needsWhiteText ? 'rgb(var(--color-drawer-text))' : 'rgb(var(--color-drawer-background))'}; font-weight: 700; font-size: var(--text-sm); border-radius: var(--rounded-button); border: 1px solid ${needsWhiteText ? 'rgba(255, 255, 255, 0.95)' : 'rgb(var(--color-drawer-text))'}; ${needsWhiteText ? 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);' : ''}">
          FREE
        </div>
      `;
    }

    /**
     * Get SVG icon for slide
     */
    getSlideIcon(iconType, hasVideo = false) {
      // Use dark color for icons when on video background (white container)
      const iconColor = hasVideo ? '#333' : 'rgb(var(--color-drawer-text))';

      const icons = {
        plate: `
          <svg style="width: 2rem; height: 2rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
          </svg>
        `,
        letter: `
          <svg style="width: 2rem; height: 2rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        `,
        video: `
          <svg style="width: 2rem; height: 2rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
          </svg>
        `
      };

      return icons[iconType] || icons.letter;
    }

    /**
     * Build modal HTML with full details
     */
    buildModalHTML() {
      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Personalisation Options Explained" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Personalisation Options</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-drawer-text)); border-radius: var(--rounded-card); margin-bottom: 1rem;">
                    <svg style="width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="none" stroke="url(#modalIconGradient)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <defs>
                        <linearGradient id="modalIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="5%" stop-color="rgba(249, 119, 255, 1)" />
                          <stop offset="50%" stop-color="rgba(54, 212, 255, 1)" />
                          <stop offset="100%" stop-color="rgba(43, 252, 135, 1)" />
                        </linearGradient>
                      </defs>
                      <path d="M12 3l1.5 3.5L17 8l-3.5 1.5L12 13l-1.5-3.5L7 8l3.5-1.5L12 3z"/>
                      <path d="M5 16l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/>
                      <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/>
                    </svg>
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    Personalise Your Ride-On
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    Make it truly special with personalised touches that bring a smile every time
                  </p>
                </div>

                <!-- Option 1: Number Plate -->
                <div style="padding: 1.5rem; border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card); background: rgb(var(--color-drawer-background));">
                  <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                    <div style="display: flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card);">
                      ${this.getSlideIcon('plate')}
                    </div>
                    <div style="flex: 1;">
                      <h4 class="font-bold text-base" style="margin: 0;">
                        Personalised Number Plate
                      </h4>
                    </div>
                  </div>
                  <p class="text-sm" style="margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-text) / 0.7);">
                    Choose from over 100 designs and add their name to make the ride-on truly theirs
                  </p>
                  <ul class="text-sm" style="list-style: none; padding: 0; margin: 0;">
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>45 country flag designs to choose from</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Fully customisable with their name</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Configure in checkout - takes 2 minutes</span>
                    </li>
                  </ul>
                </div>

                <!-- Option 2: Personalised Letter -->
                <div style="padding: 1.5rem; border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card); background: rgb(var(--color-drawer-background));">
                  <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                    <div style="display: flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card);">
                      ${this.getSlideIcon('letter')}
                    </div>
                    <div style="flex: 1;">
                      <h4 class="font-bold text-base" style="margin: 0 0.5rem 0 0; display: inline;">
                        ${this.getLetterThemeConfig().modalTitle}
                      </h4>
                      <span style="display: inline-flex; align-items: center; padding: 0.125rem 0.5rem; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase; border-radius: var(--rounded-button);">FREE</span>
                    </div>
                  </div>
                  <p class="text-sm" style="margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-text) / 0.7);">
                    ${this.getLetterThemeConfig().modalDescription}
                  </p>
                  <ul class="text-sm" style="list-style: none; padding: 0; margin: 0;">
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Beautifully designed templates to choose from</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Personalised with child's name and details</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Printed letter delivered with your ride-on</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Completely FREE with every ride-on</span>
                    </li>
                  </ul>
                </div>

                <!-- Option 3: Santa Video - Dark Theme (only shown in christmas mode) -->
                ${this.letterPromoTheme === 'christmas' ? `
                <div style="padding: 1.5rem; border-radius: var(--rounded-card); background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background));">
                  <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                    <div style="display: flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: rgb(var(--color-drawer-background) / 0.15); border-radius: var(--rounded-card);">
                      <svg style="width: 2rem; height: 2rem; color: rgb(var(--color-drawer-background));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div style="flex: 1;">
                      <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                        <h4 class="font-bold text-base" style="margin: 0; color: rgb(var(--color-drawer-background));">
                          Personalised Santa Video
                        </h4>
                        <span style="display: inline-flex; align-items: center; padding: 0.125rem 0.5rem; background: rgb(var(--color-drawer-background)); color: rgb(var(--color-drawer-text)); font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase; border-radius: var(--rounded-button);">PREMIUM</span>
                      </div>
                      <div style="margin-top: 0.25rem; font-weight: 700; font-size: var(--text-lg); color: rgb(var(--color-drawer-background));">
                        ${(() => {
                          const videoSlide = this.slides.find(s => s.id === 'santa-video');
                          if (videoSlide?.product?.price) {
                            return `£${(videoSlide.product.price / 100).toFixed(0)}`;
                          }
                          return '';
                        })()}
                      </div>
                    </div>
                  </div>
                  <p class="text-sm" style="margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-background) / 0.8);">
                    Beautiful Disney/Pixar-style animated video of Santa putting a bow on your child's ride-on in YOUR living room - kids love this charming style!
                  </p>
                  <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-background));">
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Beautiful animated Santa in YOUR living room</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Shows the exact ride-on you purchased</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Personalised with child's name</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Free 3-5 days - Next Day available</span>
                    </li>
                  </ul>
                </div>
                ` : ''}

                <!-- How to Configure -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <h4 class="font-bold text-base" style="margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem;">
                    <svg style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    How to Configure
                  </h4>
                  <ol class="text-sm" style="list-style: none; padding: 0; margin: 0; counter-reset: step-counter;">
                    <li style="padding: 0.75rem 0; display: flex; gap: 1rem; counter-increment: step-counter; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                      <span style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); border-radius: 50%; font-weight: 700; font-size: 0.875rem;">1</span>
                      <span style="flex: 1; padding-top: 0.125rem;">Add your ride-on and desired personalisation options to cart</span>
                    </li>
                    <li style="padding: 0.75rem 0; display: flex; gap: 1rem; counter-increment: step-counter; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                      <span style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); border-radius: 50%; font-weight: 700; font-size: 0.875rem;">2</span>
                      <span style="flex: 1; padding-top: 0.125rem;">In your cart, click "Configure" next to each personalisation item</span>
                    </li>
                    <li style="padding: 0.75rem 0; display: flex; gap: 1rem; counter-increment: step-counter; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                      <span style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); border-radius: 50%; font-weight: 700; font-size: 0.875rem;">3</span>
                      <span style="flex: 1; padding-top: 0.125rem;">Choose your design and enter personalisation details</span>
                    </li>
                    <li style="padding: 0.75rem 0; display: flex; gap: 1rem; counter-increment: step-counter; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                      <span style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); border-radius: 50%; font-weight: 700; font-size: 0.875rem;">4</span>
                      <span style="flex: 1; padding-top: 0.125rem;">Complete checkout - we'll prepare your personalised items</span>
                    </li>
                  </ol>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      const track = this.container.querySelector('.pw-carousel-track');
      const prevBtn = this.container.querySelector('.pw-carousel-arrow--prev');
      const nextBtn = this.container.querySelector('.pw-carousel-arrow--next');
      const dots = this.container.querySelectorAll('.pw-carousel-dot');
      const playPauseBtn = this.container.querySelector('.pw-carousel-play-pause');

      // Navigation buttons
      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.previousSlide());
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.nextSlide());
      }

      // Dot indicators
      dots.forEach(dot => {
        dot.addEventListener('click', (e) => {
          const slideIndex = parseInt(e.target.dataset.slide);
          this.goToSlide(slideIndex);
        });
      });

      // Play/Pause button
      if (playPauseBtn) {
        playPauseBtn.addEventListener('click', () => this.toggleAutoPlay());
      }

      // Hover to pause
      if (track) {
        track.addEventListener('mouseenter', () => this.pauseAutoPlay());
        track.addEventListener('mouseleave', () => this.resumeAutoPlay());
      }

      // Use Intersection Observer for instant dot updates
      if (track && 'IntersectionObserver' in window) {
        const slides = this.container.querySelectorAll('.pw-carousel-slide');

        const observerOptions = {
          root: track,
          threshold: 0.5 // Trigger when 50% of slide is visible
        };

        this.slideObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const slideIndex = parseInt(entry.target.dataset.slideIndex);
              this.updateCurrentSlide(slideIndex);
            }
          });
        }, observerOptions);

        // Observe all slides
        slides.forEach(slide => this.slideObserver.observe(slide));
      }

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Make carousel cards clickable to open modal
      const cards = this.container.querySelectorAll('.pw-carousel-card');
      cards.forEach(card => {
        // Click event
        card.addEventListener('click', () => {
          const modalId = card.getAttribute('aria-controls');
          const modal = document.getElementById(modalId);
          if (modal) {
            // Trigger modal open via aria-expanded attribute
            card.setAttribute('aria-expanded', 'true');
            // The x-modal component will handle the actual opening
          }
        });

        // Keyboard event (Enter or Space)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });

        // Hover effect - add subtle grey border
        card.addEventListener('mouseenter', () => {
          card.style.borderColor = 'rgb(var(--color-drawer-text) / 0.3)';
          card.style.borderWidth = '2px';
        });

        card.addEventListener('mouseleave', () => {
          card.style.borderColor = 'rgb(var(--color-drawer-text) / 0.08)';
          card.style.borderWidth = '1px';
        });
      });
    }

    /**
     * Start auto-play rotation
     */
    startAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
      }

      this.autoPlayInterval = setInterval(() => {
        if (this.isAutoPlaying) {
          this.nextSlide();
        }
      }, this.rotationDelay);
    }

    /**
     * Stop auto-play
     */
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    /**
     * Update play/pause button UI
     */
    updatePlayPauseButton() {
      const pauseIcon = this.container.querySelector('.pw-carousel-pause-icon');
      const playIcon = this.container.querySelector('.pw-carousel-play-icon');

      if (!pauseIcon || !playIcon) return;

      if (this.isAutoPlaying) {
        pauseIcon.style.display = 'block';
        playIcon.style.display = 'none';
      } else {
        pauseIcon.style.display = 'none';
        playIcon.style.display = 'block';
      }
    }

    /**
     * Pause auto-play (on hover)
     */
    pauseAutoPlay() {
      // Don't pause if user manually paused already
      if (this.isManuallyPaused) return;

      this.isAutoPlaying = false;
      this.updatePlayPauseButton();
    }

    /**
     * Resume auto-play (on leave)
     */
    resumeAutoPlay() {
      // Don't resume if user manually paused
      if (this.isManuallyPaused) return;

      this.isAutoPlaying = true;
      this.updatePlayPauseButton();
    }

    /**
     * Toggle auto-play (manual button click)
     */
    toggleAutoPlay() {
      this.isAutoPlaying = !this.isAutoPlaying;
      this.isManuallyPaused = !this.isAutoPlaying; // Track manual pause state

      this.updatePlayPauseButton();
      this.emit('carousel:autoplay-toggled', { isPlaying: this.isAutoPlaying });
    }

    /**
     * Go to next slide
     */
    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    }

    /**
     * Go to previous slide
     */
    previousSlide() {
      const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex);
    }

    /**
     * Go to specific slide using scroll
     */
    goToSlide(index) {
      if (index === this.currentSlide) return;

      const track = this.container.querySelector('.pw-carousel-track');
      const slides = this.container.querySelectorAll('.pw-carousel-slide');
      const slide = slides[index];

      if (slide && track) {
        // Calculate the scroll position within the track only (not the page)
        const slideOffset = slide.offsetLeft;

        // Scroll the track container only, not the entire page
        track.scrollTo({
          left: slideOffset,
          behavior: 'smooth'
        });
      }

      // Update will happen via IntersectionObserver
    }

    /**
     * Update current slide indicator based on scroll position
     */
    updateCurrentSlide(index) {
      if (index === this.currentSlide) return;

      const slides = this.container.querySelectorAll('.pw-carousel-slide');
      const dots = this.container.querySelectorAll('.pw-carousel-dot');

      // Update slides
      slides[this.currentSlide]?.classList.remove('pw-carousel-slide--active');
      slides[this.currentSlide]?.setAttribute('aria-hidden', 'true');

      slides[index]?.classList.add('pw-carousel-slide--active');
      slides[index]?.setAttribute('aria-hidden', 'false');

      // Update dots with animated width expansion
      dots[this.currentSlide]?.classList.remove('pw-carousel-dot--active');
      dots[this.currentSlide]?.setAttribute('aria-selected', 'false');
      dots[this.currentSlide].style.width = '10px';
      dots[this.currentSlide].style.background = 'rgb(var(--color-drawer-background) / 0.7)';

      dots[index]?.classList.add('pw-carousel-dot--active');
      dots[index]?.setAttribute('aria-selected', 'true');
      dots[index].style.width = '24px';
      dots[index].style.background = 'rgb(var(--color-drawer-background))';

      // Update current slide
      this.currentSlide = index;

      this.emit('carousel:slide-changed', { slideIndex: index, slide: this.slides[index] });
    }

    /**
     * Handle keyboard navigation
     */
    handleKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case ' ':
          e.preventDefault();
          this.toggleAutoPlay();
          break;
      }
    }

    /**
     * Update widget
     */
    update(data) {
      if (data.rotationDelay) {
        this.rotationDelay = data.rotationDelay;
        this.stopAutoPlay();
        this.startAutoPlay();
      }
    }

    /**
     * Cleanup
     */
    destroy() {
      this.stopAutoPlay();

      // Disconnect intersection observer
      if (this.slideObserver) {
        this.slideObserver.disconnect();
      }

      super.destroy();
    }
  }

  // Expose widget class
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.PersonalizationCarouselWidget = PersonalizationCarouselWidget;

})(window);
